import { useCallback, useEffect, useState } from "react";
import { BarChart3, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const BUCKETS = [["as_is", "as-is"], ["light", "light edit"], ["heavy", "heavy edit"], ["independent", "own words"]];
const n = (v) => (v == null ? "—" : v);

/** Part B §7 — was the AI pre-reply used? Per week + per handler, from what the portal recorded at Send. */
export function DraftVsSent({ api }) {
  const [weeks, setWeeks] = useState(4);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.draftVsSent(weeks)); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [api, weeks]);
  useEffect(() => { load(); }, [load]);

  const Th = ({ children, right }) => <th className={cn("px-2 py-1 font-medium text-slate-500 text-[10px] whitespace-nowrap", right ? "text-right" : "text-left")}>{children}</th>;
  const Td = ({ children, right, strong }) => <td className={cn("px-2 py-1 whitespace-nowrap", right && "text-right tabular-nums", strong && "font-semibold")}>{children}</td>;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto text-xs" data-testid="draft-vs-sent">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-slate-50 text-[11px] sticky top-0">
        <BarChart3 className="w-3.5 h-3.5 text-violet-700" />
        <span className="font-medium">Draft vs sent</span>
        <span className="text-muted-foreground">— replies sent from the portal only; Outlook replies aren't visible here</span>
        <span className="ml-auto flex items-center gap-1">
          {[2, 4, 8, 12].map(w => (
            <button key={w} type="button" onClick={() => setWeeks(w)} className={cn("px-1.5 h-6 rounded border text-[10px]", weeks === w ? "bg-slate-800 text-white border-slate-800" : "bg-white hover:bg-slate-100")} data-testid={`dvs-weeks-${w}`}>{w}w</button>
          ))}
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={load} disabled={loading} data-testid="dvs-refresh">{loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}</Button>
        </span>
      </div>
      {!data ? <div className="p-6 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading…</div> : (
        <div className="p-3 space-y-4">
          <div className="flex flex-wrap gap-3 text-[11px]" data-testid="dvs-totals">
            <span><b>{data.drafts}</b> drafts</span>
            <span><b>{data.sent_via_portal}</b> replied via the portal</span>
            {data.tones?.length > 0 && <span className="text-muted-foreground">tones: {data.tones.map(([t, c]) => `${t} ${c}`).join(" · ")}</span>}
          </div>
          <section>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Per week</div>
            <div className="overflow-x-auto"><table className="w-full min-w-[720px] border rounded text-[11px]" data-testid="dvs-weeks-table">
              <thead className="bg-slate-50"><tr><Th>Week of</Th><Th right>Drafts</Th><Th right>Holding</Th><Th right>Sent via portal</Th>{BUCKETS.map(([k, l]) => <Th key={k} right>{l}</Th>)}<Th right>Words draft → sent</Th><Th right>Median mins</Th></tr></thead>
              <tbody className="divide-y">
                {data.weeks.map(w => (
                  <tr key={w.week} data-testid={`dvs-week-${w.week.replace(/\s/g, "-")}`}>
                    <Td strong>{w.label || w.week}</Td><Td right>{w.drafts}</Td><Td right>{w.holding}</Td><Td right>{w.sent_via_portal}</Td>
                    {BUCKETS.map(([k]) => <Td key={k} right>{w[k]}</Td>)}
                    <Td right>{n(w.median_words_draft)} → {n(w.median_words_sent)}</Td><Td right>{n(w.median_minutes)}</Td>
                  </tr>
                ))}
                {!data.weeks.length && <tr><td colSpan={11} className="px-2 py-3 text-center text-muted-foreground">No drafts in this window.</td></tr>}
              </tbody>
            </table></div>
          </section>
          <section>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Per handler (portal replies to drafted emails)</div>
            <div className="overflow-x-auto"><table className="w-full min-w-[720px] border rounded text-[11px]" data-testid="dvs-handlers-table">
              <thead className="bg-slate-50"><tr><Th>Handler</Th><Th right>Replies</Th><Th right>Used draft</Th>{BUCKETS.map(([k, l]) => <Th key={k} right>{l}</Th>)}<Th right>Opening kept</Th><Th right>Words sent vs draft</Th><Th right>Median mins</Th></tr></thead>
              <tbody className="divide-y">
                {data.handlers.map(h => (
                  <tr key={h.handler} data-testid={`dvs-handler-${h.handler.replace(/\s/g, "-")}`}>
                    <Td strong>{h.handler}</Td><Td right>{h.replies}</Td><Td right>{h.used} ({h.used_pct}%)</Td>
                    {BUCKETS.map(([k]) => <Td key={k} right>{h[k]}</Td>)}
                    <Td right>{h.opening_kept}</Td><Td right>{n(h.median_words_sent)} vs {n(h.median_words_draft)}</Td><Td right>{n(h.median_minutes)}</Td>
                  </tr>
                ))}
                {!data.handlers.length && <tr><td colSpan={10} className="px-2 py-3 text-center text-muted-foreground">Nothing recorded yet — it fills in as replies are sent from the portal.</td></tr>}
              </tbody>
            </table></div>
          </section>
          <div className="grid gap-3 md:grid-cols-3 text-[11px]">
            <section className="border rounded p-2" data-testid="dvs-removed">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Handlers removed</div>
              {data.removed.length ? data.removed.map(([k, c]) => <div key={k} className="flex justify-between"><span>{k}</span><b>{c}</b></div>) : <div className="text-muted-foreground">—</div>}
            </section>
            <section className="border rounded p-2" data-testid="dvs-added">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Handlers added</div>
              {data.added.length ? data.added.map(([k, c]) => <div key={k} className="flex justify-between"><span>{k}</span><b>{c}</b></div>) : <div className="text-muted-foreground">—</div>}
            </section>
            <section className="border rounded p-2" data-testid="dvs-skips">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Autopilot didn't draft because</div>
              {data.skips.length ? data.skips.map(([k, c]) => <div key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><b>{c}</b></div>) : <div className="text-muted-foreground">—</div>}
              {data.tics_in_drafts.length > 0 && <div className="mt-2 pt-1 border-t text-[10px] text-muted-foreground">Still in drafts: {data.tics_in_drafts.map(([k, c]) => `${k} ${c}`).join(" · ")}</div>}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
